'use client';

import { useRef } from 'react';

import { SafeImage, Text } from '@/ui';
import { useContainerQuery, validateImage } from '@lib';
import { clsx } from 'clsx';
import { useMediaQuery } from 'usehooks-ts';

import { CardProps } from '.';

export function CardTitle({
  title,
  titleTag = 'h3',
  layout,
  icon,
  classNames,
  subtitle,
  subtitleDecoration,
  contentAlignment,
  href,
  children,
}: CardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const smallContainer = useContainerQuery(ref, '(max-width: 393px)');
  const mobile = useMediaQuery('(max-width: 393px)');

  if (!title && !subtitle) {
    return children;
  }

  return (
    <div
      ref={ref}
      className={clsx(
        'flex w-full flex-col gap-0.5',
        {
          'absolute inset-0 bg-linear-to-b p-6 text-white transition': layout === 'compact',
          'hover:bg-black/25': layout === 'compact' && href,
          'text-muted bg-muted w-max animate-pulse rounded': !title,
          'items-center text-center': contentAlignment?.alignment?.horizontalAlignment === 'center',
          'items-end text-end': contentAlignment?.alignment?.horizontalAlignment === 'right',
          'items-start text-start':
            contentAlignment?.alignment?.horizontalAlignment === 'left' ||
            !contentAlignment?.alignment?.horizontalAlignment,
          'justify-center from-transparent via-black/50 to-transparent':
            contentAlignment?.alignment?.verticalAlignment === 'middle',
          'justify-start from-black/50 to-transparent': contentAlignment?.alignment?.verticalAlignment === 'top',
          'justify-end from-transparent to-black/50':
            contentAlignment?.alignment?.verticalAlignment === 'bottom' ||
            !contentAlignment?.alignment?.verticalAlignment,
        },
        classNames?.title,
      )}
    >
      {icon &&
        (typeof icon?.svg === 'string' ? (
          <div className="mb-3 [&>svg]:size-16" dangerouslySetInnerHTML={{ __html: icon.svg }} />
        ) : (
          <div className="mb-3">{icon?.svg}</div>
        ))}

      {subtitle && (
        <Text
          element={'p'}
          elementStyle="h6"
          className={clsx(
            classNames?.subTitle,
            {
              'py-2': subtitleDecoration,
            },
            'text-primary flex items-center gap-3 font-normal',
          )}
        >
          {validateImage(subtitleDecoration) && (
            <SafeImage
              src={subtitleDecoration}
              className={'h-5 w-5 object-contain'}
              width={20}
              height={20}
              sizes={'100px'}
            />
          )}
          {subtitle}
        </Text>
      )}

      <Text element={titleTag} className="gap-1 border-none" elementStyle={!mobile && smallContainer ? 'h3' : 'h2'}>
        {title || 'Loading...'}
      </Text>

      {children}
    </div>
  );
}
